import { Link } from 'react-router-dom'
import { Bot } from 'lucide-react'
import { InternalNameIcon } from '@/components/icons'
import type { Agent } from '@/types'
import { AgentActionsMenu } from './AgentActionsMenu'
import { AgentCategoryBadge, AgentVersionBadge } from './AgentBadges'

interface AgentCardProps {
  projectId: string
  agent: Agent
}

export function AgentCard({ projectId, agent }: AgentCardProps) {
  const titleId = `agent-card-title-${agent.id}`
  const detailPath = `/projects/${projectId}/workflows/agents/${agent.id}`

  return (
    <article
      aria-labelledby={titleId}
      className="relative flex h-full flex-col gap-3 rounded-2xl border border-border bg-card p-5 transition-shadow hover:shadow-md"
      data-testid={`agent-card-${agent.name}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground">
            <Bot className="h-4 w-4" />
          </span>
          <h3 id={titleId} className="min-w-0 truncate text-sm font-bold text-foreground">
            <Link
              to={detailPath}
              className="truncate rounded-sm after:absolute after:inset-0 after:content-[''] hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              {agent.displayName}
            </Link>
          </h3>
        </div>
        <div className="relative z-10">
          <AgentActionsMenu projectId={projectId} agent={agent} />
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-1.5">
        <AgentCategoryBadge agent={agent} />
        {agent.latestVersion && <AgentVersionBadge version={agent.latestVersion} />}
      </div>

      {agent.description && <p className="line-clamp-2 text-sm text-muted-foreground">{agent.description}</p>}

      <span className="mt-auto flex items-center gap-1.5 pt-1 font-mono text-xs text-muted-foreground">
        <InternalNameIcon className="h-3.5 w-3.5 shrink-0" />
        <span className="truncate">{agent.name}</span>
      </span>
    </article>
  )
}
